import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import ChecksTable from 'dashboard/components/ChecksTable';
import Pii from 'dashboard/components/Pii';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { VerificationDigitalIdSerpro } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import { paragraphMixins } from 'design-system/mixins/text';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const InfoSection = styled('div')`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

const ImageGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${Spacing[4]};
  padding-top: ${Spacing[2]};
`;

const PhotoLabel = styled.p`
  ${paragraphMixins.lgBold};
  margin: ${Spacing[2]} 0;
  text-align: center;
`;

interface DigitalIdSerproVerificationProps extends VerificationDetailProps {
  verification: VerificationDigitalIdSerpro;
}

const DigitalIdSerproVerification = ({ verification }: DigitalIdSerproVerificationProps) => {
  const {
    status,
    cpf,
    name,
    birthdate,
    'redacted-at': redactedAt,
    'identification-number': identificationNumber,
    'expiration-date': expirationDate,
    'issue-date': issueDate,
    'qr-code-image-url': qrCodeImageUrl,
    'selfie-compare-image-url': selfieCompareImageUrl,
    'selfie-confidence-level': selfieConfidenceLevel,
    'selfie-similarity-score': selfieSimilarityScore,
  } = verification.attributes;

  const redacted = redactedAt != null;
  const showChecks =
    status != null &&
    ![
      VerificationStatus.Initiated,
      VerificationStatus.Confirmed,
      VerificationStatus.Submitted,
      VerificationStatus.Canceled,
    ].includes(status);

  const checks = verification.attributes.checks;

  return (
    <InfoSection>
      {(qrCodeImageUrl || selfieCompareImageUrl) && (
        <Pii key='photos' isRedacted={redacted} hideOnRedacted={true}>
          <ImageGridContainer>
            {qrCodeImageUrl && (
              <div key='qr-code-photo'>
                <AsyncImage
                  imageUrl={qrCodeImageUrl}
                  width='initial'
                  height='250px'
                  modalLabel='QR Code'
                  allowModalExpand={true}
                />
                <PhotoLabel>QR Code</PhotoLabel>
              </div>
            )}
            {selfieCompareImageUrl && (
              <div key='selfie-photo'>
                <AsyncImage
                  imageUrl={selfieCompareImageUrl}
                  width='initial'
                  height='250px'
                  modalLabel='Selfie photo'
                  allowModalExpand={true}
                />
                <PhotoLabel>Selfie photo</PhotoLabel>
              </div>
            )}
          </ImageGridContainer>
        </Pii>
      )}

      <StyledAttributeGrid>
        <Attribute label='CPF' value={cpf} redacted={redacted} />
        <Attribute label='Name' value={name} redacted={redacted} />
        <Attribute label='Birthdate' value={formatDate(birthdate)} redacted={redacted} />
        <Attribute label='Identification number' value={identificationNumber} redacted={redacted} />
        <Attribute label='Issue date' value={formatDate(issueDate)} redacted={redacted} />
        <Attribute label='Expiration date' value={formatDate(expirationDate)} redacted={redacted} />
        <Attribute
          label='Selfie confidence level'
          value={selfieConfidenceLevel}
          redacted={redacted}
        />
        <Attribute
          label='Selfie similarity score'
          value={selfieSimilarityScore}
          redacted={redacted}
        />
      </StyledAttributeGrid>
      {showChecks && checks && <ChecksTable checks={checks} />}
    </InfoSection>
  );
};

export default DigitalIdSerproVerification;
